
import { Component, ChangeDetectorRef, Input, OnChanges, SimpleChanges, Output, EventEmitter, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs/operators';

export interface SortChangedEvent {
  sortBy: string;
  direction: 'asc' | 'desc';
}

export interface SortControlOption {
  value: string;
  viewValue: string;
  sortIcon?: 'alpha' | 'numeric';
  /** Direction applied automatically when the user selects this option. */
  defaultDirection?: 'asc' | 'desc';
}

export const SORT_DIRECTION: {
  ASC: 'asc';
  DESC: 'desc';
} = {
  ASC: 'asc',
  DESC: 'desc'
};

@Component({
  selector: 'app-sort-control',
  templateUrl: './sort-control.component.html'
})
export class SortControlComponent implements OnInit, OnChanges {
  @Input() sortDirection: 'asc' | 'desc' | 'ASC' | 'DESC' = SORT_DIRECTION.DESC;
  @Input() sortByOptions: Array<SortControlOption>;
  @Input() sortBy: string;
  @Input() sortSelectControl = new UntypedFormControl();
  @Output() sortChanged = new EventEmitter<SortChangedEvent>();
  @Input() sortLabelVisible = true;

  /**
   * The option the select is currently showing. Prefers the control value over
   * the sortBy input, which lags behind while the parent round-trips the change.
   */
  get selectedOption(): SortControlOption | undefined {
    const value = this.sortSelectControl.value ?? this.sortBy;

    return (this.sortByOptions || []).find(o => o.value === value);
  }

  get sortIcon() {
    const iconType = this.selectedOption?.sortIcon || 'alpha';
    const iconBase = `fa-sort-${iconType}`;

    if (this.sortDirection.toLowerCase() === SORT_DIRECTION.ASC) {
      return `${iconBase}-down`;
    }

    return `${iconBase}-up`;
  }

  /**
   *
   */
  constructor(private cd: ChangeDetectorRef) {
  }

  ngOnInit() {
    this.sortSelectControl.valueChanges.pipe(
      distinctUntilChanged()
    ).subscribe((value) => {
      // Each option can declare the direction that reads naturally for it
      // (largest first for a count, A-Z for a name), so selecting a different
      // option snaps to that direction rather than inheriting the previous one.
      const defaultDirection = this.selectedOption?.defaultDirection;
      if (defaultDirection) {
        this.sortDirection = defaultDirection;
      }

      this.emitSortChanged();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const sortByChanges = changes['sortBy'];
    if (sortByChanges) {
      // Silent: this is the parent pushing state down, not a user selection, so it
      // must not apply the option's defaultDirection or echo sortChanged back.
      this.sortSelectControl.setValue(sortByChanges.currentValue, { emitEvent: false });
    }
  }

  directionChangeClicked(event: MouseEvent | TouchEvent) {
    event.stopImmediatePropagation();
    this.toggleSortDirection();
  }

  toggleSortDirection() {
    if (this.sortDirection.toLowerCase() === SORT_DIRECTION.ASC) {
      this.sortDirection = SORT_DIRECTION.DESC;
    } else {
      this.sortDirection = SORT_DIRECTION.ASC;
    }
    this.emitSortChanged();
    this.cd.markForCheck();
  }

  private emitSortChanged() {
    this.sortChanged.emit({
      sortBy: this.sortSelectControl.value,
      direction: this.sortDirection.toLowerCase() as 'asc' | 'desc'
    });
  }

}
